import { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  TextField,
  Avatar,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  Stack,
  Divider,
  IconButton,
  Grid,
} from "@mui/material";
import {
  IconDownload,
  IconMessage,
  IconAt,
  IconMessageCircle,
  IconSend,
  IconUserPlus,
  IconMoodSmile,
  IconMoodSad,
  IconMoodEmpty,
} from "@tabler/icons-react";
import EmojiPicker from "emoji-picker-react";
import Button from "../components/Button";
import UserContext from "../contexts/UserContext";
import { fetchGet, fetchPost } from "../util/helpers";

const ENGAGEMENT_TYPES = [
  { value: "dm", label: "DMs", icon: IconMessage },
  { value: "mention", label: "Mentions", icon: IconAt },
  { value: "comment", label: "Comments", icon: IconMessageCircle },
];

const SENTIMENT_CONFIG = {
  positive: { label: "Positive", color: "#10B981", icon: IconMoodSmile },
  negative: { label: "Negative", color: "#EF4444", icon: IconMoodSad },
  neutral: { label: "Neutral", color: "#6B7280", icon: IconMoodEmpty },
};

const fadeIn = {
  "@keyframes fadeInUp": {
    from: { opacity: 0, transform: "translateY(12px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
};

/** Firestore Timestamp JSON, REST _seconds, ISO string, or unix ms */
function formatEngagementDate(val) {
  if (val == null || val === "") return "";
  if (typeof val === "string") {
    const d = new Date(val);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
  }
  if (typeof val === "number") {
    const d = new Date(val);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
  }
  if (typeof val === "object") {
    const sec = val.seconds ?? val._seconds;
    const nano = val.nanoseconds ?? val._nanoseconds ?? 0;
    if (sec != null) {
      const ms = Number(sec) * 1000 + Math.floor(Number(nano) / 1e6);
      const d = new Date(ms);
      return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
    }
  }
  return "";
}

export default function EngagementInbox() {
  const { user } = useContext(UserContext);
  const [channel, setChannel] = useState("");
  const [type, setType] = useState("");
  const [messages, setMessages] = useState([]);
  const [counts, setCounts] = useState({ dm: 0, mention: 0, comment: 0 });
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [respondTo, setRespondTo] = useState(null);
  const [responseText, setResponseText] = useState("");
  const [showReplyEmoji, setShowReplyEmoji] = useState(false);
  const [assignDialog, setAssignDialog] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [report, setReport] = useState(null);

  const loadMessages = () => {
    const params = new URLSearchParams();
    if (channel) params.set("channel", channel);
    if (type) params.set("type", type);
    fetchGet(`/engagement/messages?${params.toString()}`, setLoading)
      .then((res) => { if (res?.result) setMessages(res.result); })
      .catch(() => {});
  };

  useEffect(() => {
    loadMessages();
    fetchGet("/engagement/messages/counts").then((res) => { if (res?.result) setCounts(res.result); }).catch(() => {});
    fetchGet("/team/channels").then((res) => { if (res?.result) setChannels(res.result); }).catch(() => {});
    fetchGet("/team/members").then((res) => { if (res?.result) setTeamMembers(res.result); }).catch(() => {});
  }, []);

  useEffect(() => {
    loadMessages();
  }, [channel, type]);

  const handleRespond = async () => {
    if (!respondTo || !responseText.trim()) return;
    try {
      await fetchPost(`/engagement/messages/${respondTo}/respond`, { response: responseText });
      setRespondTo(null);
      setResponseText("");
      loadMessages();
    } catch (e) {
      console.error("Error responding:", e);
    }
  };

  const handleAssign = async (memberId) => {
    if (!assignDialog) return;
    try {
      await fetchPost(`/engagement/messages/${assignDialog}/assign`, { assignedTo: memberId });
      setAssignDialog(null);
      loadMessages();
    } catch (e) {
      console.error("Error assigning:", e);
    }
  };

  const handleDownloadReport = async () => {
    try {
      const res = await fetchGet("/engagement/report");
      if (res?.result) {
        const r = res.result;
        const csv = `Metric,Value\nTotal Messages,${r.totalMessages}\nResponded Messages,${r.respondedMessages}\nAvg Response Time (hours),${r.avgResponseTimeHours}\nResponse Rate (%),${r.responseRate}`;
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "engagement_report.csv";
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) {
      console.error("Error downloading report:", e);
    }
  };

  const totalIncoming = messages.length;
  const incomingRate = totalIncoming > 0 ? (totalIncoming / 30).toFixed(1) : "0";
  const unreadCount = (counts.dm || 0) + (counts.mention || 0) + (counts.comment || 0);
  const responseRate = totalIncoming > 0
    ? Math.round((messages.filter((m) => m.response).length / totalIncoming) * 100)
    : 0;

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, minHeight: "100vh", bgcolor: "#FAFBFC", ...fadeIn }}>
      {/* ── Gradient Header Banner ── */}
      <Paper
        elevation={0}
        sx={{
          background: "linear-gradient(135deg, #7C3AED 0%, #A855F7 50%, #C084FC 100%)",
          borderRadius: 4,
          px: { xs: 3, md: 5 },
          py: { xs: 3, md: 4 },
          mb: 3,
          boxShadow: "0 8px 32px rgba(124, 58, 237, 0.18)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: -40,
            right: -40,
            width: 180,
            height: 180,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.08)",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: -30,
            left: "40%",
            width: 120,
            height: 120,
            borderRadius: "50%",
            background: "rgba(255,255,255,0.05)",
          }}
        />
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2, position: "relative", zIndex: 1 }}>
          <Box>
            <Typography variant="h4" sx={{ color: "#fff", fontWeight: 800, letterSpacing: "-0.02em" }}>
              Engagement / Inbox
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)", mt: 0.5, fontSize: 14 }}>
              Monitor and respond to messages across all your social channels
            </Typography>
          </Box>
          <Button
            style={{ borderRadius: 12, padding: "10px 24px", fontSize: 13, background: "rgba(255,255,255,0.2)", backdropFilter: "blur(8px)", color: "#fff", border: "1px solid rgba(255,255,255,0.25)" }}
            onClick={handleDownloadReport}
          >
            <IconDownload size={16} style={{ marginRight: 8 }} /> Download Report
          </Button>
        </Box>
      </Paper>

      {/* ── Stats Summary Cards ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: "Total Messages", value: totalIncoming, accent: "#7C3AED", icon: <IconMessage size={20} /> },
          { label: "Unread", value: unreadCount, accent: "#EF4444", icon: <IconAt size={20} /> },
          { label: "Response Rate", value: `${responseRate}%`, accent: "#10B981", icon: <IconMoodSmile size={20} /> },
        ].map((stat) => (
          <Grid item xs={12} sm={4} key={stat.label}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 3,
                border: "1px solid rgba(0,0,0,0.06)",
                boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                gap: 2,
                animation: "fadeInUp 0.4s ease-out both",
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: `linear-gradient(135deg, ${stat.accent}18, ${stat.accent}30)`,
                  color: stat.accent,
                }}
              >
                {stat.icon}
              </Box>
              <Box>
                <Typography variant="body2" sx={{ color: "text.secondary", fontSize: 12, fontWeight: 500 }}>
                  {stat.label}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: stat.accent, lineHeight: 1.2 }}>
                  {stat.value}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* ── Filter Row (frosted glass) ── */}
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          gap: 2,
          mb: 3,
          flexWrap: "wrap",
          alignItems: "center",
          p: 2,
          borderRadius: 3,
          background: "rgba(255,255,255,0.7)",
          backdropFilter: "blur(12px)",
          border: "1px solid rgba(0,0,0,0.06)",
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        }}
      >
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Social Channel</InputLabel>
          <Select
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            label="Social Channel"
            sx={{ borderRadius: 2.5, bgcolor: "#fff" }}
          >
            <MenuItem value="">All Channels</MenuItem>
            {channels.map((ch) => (
              <MenuItem key={ch.id} value={ch.name || ch.id}>{ch.name || ch.platform}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <Box sx={{ display: "flex", gap: 1 }}>
          {ENGAGEMENT_TYPES.map((et) => (
            <Chip
              key={et.value}
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <et.icon size={14} />
                  {et.label}
                  <Badge
                    badgeContent={counts[et.value]}
                    color="error"
                    sx={{ ml: 1, "& .MuiBadge-badge": { position: "relative", transform: "none", fontSize: 10, minWidth: 18, height: 18 } }}
                  />
                </Box>
              }
              variant={type === et.value ? "filled" : "outlined"}
              color={type === et.value ? "primary" : "default"}
              onClick={() => setType(type === et.value ? "" : et.value)}
              sx={{
                cursor: "pointer",
                px: 1.5,
                height: 36,
                borderRadius: 2.5,
                fontWeight: 600,
                fontSize: 13,
                transition: "all 0.2s",
                ...(type === et.value
                  ? { background: "linear-gradient(135deg, #7C3AED, #A855F7)", color: "#fff", border: "none", boxShadow: "0 2px 8px rgba(124,58,237,0.25)" }
                  : { bgcolor: "#fff", "&:hover": { bgcolor: "#F3F0FF", borderColor: "#A855F7" } }),
              }}
            />
          ))}
        </Box>
      </Paper>

      {/* ── Section Label ── */}
      <Typography variant="overline" sx={{ color: "text.secondary", fontSize: 11, fontWeight: 700, letterSpacing: 1.5, display: "block", mb: 1.5, pl: 0.5 }}>
        Messages
      </Typography>

      {/* ── Message Cards ── */}
      <Stack spacing={2}>
        {messages.length === 0 && !loading && (
          <Paper
            elevation={0}
            sx={{
              p: 5,
              textAlign: "center",
              borderRadius: 3,
              border: "1px solid rgba(0,0,0,0.06)",
              boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
            }}
          >
            <IconMoodEmpty size={40} style={{ color: "#D1D5DB", marginBottom: 8 }} />
            <Typography variant="body1" color="text.secondary">No engagement messages found</Typography>
          </Paper>
        )}
        {messages.map((msg, idx) => {
          const sentimentCfg = SENTIMENT_CONFIG[msg.sentiment] || SENTIMENT_CONFIG.neutral;
          const SentimentIcon = sentimentCfg.icon;
          const createdLabel = formatEngagementDate(msg.createdAt);
          return (
            <Paper
              key={msg.id}
              elevation={0}
              sx={{
                p: 0,
                borderRadius: 3,
                border: "1px solid rgba(0,0,0,0.06)",
                boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                borderLeft: `4px solid ${sentimentCfg.color}`,
                overflow: "hidden",
                animation: `fadeInUp 0.35s ease-out ${idx * 0.06}s both`,
                transition: "box-shadow 0.2s, transform 0.2s",
                "&:hover": {
                  boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
                  transform: "translateY(-1px)",
                },
              }}
            >
              <Box sx={{ p: 2.5 }}>
                <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
                  {/* Avatar with gradient ring */}
                  <Box
                    sx={{
                      p: "3px",
                      borderRadius: "50%",
                      background: `linear-gradient(135deg, ${sentimentCfg.color}, ${sentimentCfg.color}88)`,
                      flexShrink: 0,
                    }}
                  >
                    <Avatar
                      src={msg.authorAvatar}
                      sx={{
                        width: 42,
                        height: 42,
                        border: "2px solid #fff",
                        fontSize: 16,
                        fontWeight: 700,
                      }}
                    >
                      {msg.authorName?.[0] || "?"}
                    </Avatar>
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 14 }}>{msg.authorName}</Typography>
                      <Chip
                        label={msg.type}
                        size="small"
                        sx={{
                          fontSize: 10,
                          height: 22,
                          fontWeight: 600,
                          bgcolor: "#F3F0FF",
                          color: "#7C3AED",
                          borderRadius: 1.5,
                        }}
                      />
                      <Chip
                        label={msg.channel}
                        size="small"
                        sx={{
                          fontSize: 10,
                          height: 22,
                          fontWeight: 600,
                          bgcolor: "#EEF2FF",
                          color: "#4F46E5",
                          borderRadius: 1.5,
                        }}
                      />
                      {msg.assignedTo && (
                        <Chip
                          label={`Assigned: ${msg.assignedTo}`}
                          size="small"
                          sx={{
                            fontSize: 10,
                            height: 22,
                            fontWeight: 600,
                            bgcolor: "#DBEAFE",
                            color: "#1D4ED8",
                            borderRadius: 1.5,
                          }}
                        />
                      )}
                    </Box>

                    <Typography variant="body2" sx={{ mb: 1, lineHeight: 1.6, color: "#374151" }}>{msg.content}</Typography>

                    {createdLabel && (
                      <Typography variant="caption" sx={{ color: "text.secondary", fontSize: 11 }}>
                        {createdLabel}
                      </Typography>
                    )}

                    {msg.response && (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          mt: 1.5,
                          bgcolor: "#F0FDF4",
                          borderRadius: 2,
                          borderLeft: "3px solid #10B981",
                        }}
                      >
                        <Typography variant="caption" sx={{ color: "#059669", fontWeight: 600, fontSize: 11 }}>Your Response</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, color: "#065F46" }}>{msg.response}</Typography>
                      </Paper>
                    )}

                    {respondTo === msg.id && (
                      <Box
                        sx={{
                          mt: 1.5,
                          p: 1.5,
                          borderRadius: 2.5,
                          bgcolor: "#FAFBFC",
                          border: "1px solid rgba(0,0,0,0.06)",
                          position: "relative",
                        }}
                      >
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Type your response..."
                            value={responseText}
                            onChange={(e) => setResponseText(e.target.value)}
                            multiline
                            minRows={2}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                                bgcolor: "#fff",
                              },
                            }}
                          />
                          <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={() => setShowReplyEmoji((prev) => !prev)}
                              sx={{
                                width: 36,
                                height: 36,
                                bgcolor: showReplyEmoji ? "rgba(124,58,237,0.1)" : "transparent",
                                color: showReplyEmoji ? "#7C3AED" : "#9CA3AF",
                                "&:hover": { bgcolor: "rgba(124,58,237,0.08)", color: "#7C3AED" },
                              }}
                            >
                              <IconMoodSmile size={18} />
                            </IconButton>
                            <IconButton
                              onClick={handleRespond}
                              sx={{
                                background: "linear-gradient(135deg, #7C3AED, #A855F7)",
                                color: "#fff",
                                width: 36,
                                height: 36,
                                "&:hover": { background: "linear-gradient(135deg, #6D28D9, #9333EA)" },
                              }}
                            >
                              <IconSend size={18} />
                            </IconButton>
                          </Box>
                        </Box>
                        {showReplyEmoji && (
                          <Box sx={{
                            position: "absolute", zIndex: 10, bottom: "100%", right: 0, mb: 0.5,
                            borderRadius: 3, overflow: "hidden",
                            boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
                          }}>
                            <EmojiPicker
                              onEmojiClick={(emojiData) => {
                                setResponseText((prev) => prev + emojiData.emoji);
                                setShowReplyEmoji(false);
                              }}
                              width={320}
                              height={380}
                            />
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>

                  {/* Sentiment badge — top right */}
                  <Chip
                    label={sentimentCfg.label}
                    size="small"
                    icon={<SentimentIcon size={14} />}
                    sx={{
                      background: `linear-gradient(135deg, ${sentimentCfg.color}15, ${sentimentCfg.color}25)`,
                      color: sentimentCfg.color,
                      fontWeight: 700,
                      fontSize: 11,
                      borderRadius: 2,
                      border: `1px solid ${sentimentCfg.color}30`,
                      flexShrink: 0,
                    }}
                  />
                </Box>
              </Box>

              {/* Action bar */}
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  px: 2.5,
                  py: 1.5,
                  borderTop: "1px solid rgba(0,0,0,0.05)",
                  bgcolor: "#FAFBFC",
                }}
              >
                {!msg.response && (
                  <Button
                    style={{ borderRadius: 10, padding: "6px 16px", fontSize: 12, fontWeight: 600 }}
                    gradient
                    onClick={() => { setRespondTo(respondTo === msg.id ? null : msg.id); setResponseText(""); setShowReplyEmoji(false); }}
                  >
                    <IconSend size={14} style={{ marginRight: 6 }} /> Respond
                  </Button>
                )}
                <Button
                  style={{
                    borderRadius: 10,
                    padding: "6px 16px",
                    fontSize: 12,
                    fontWeight: 600,
                    background: "linear-gradient(135deg, #EEF2FF, #E0E7FF)",
                    color: "#4F46E5",
                    border: "1px solid #C7D2FE",
                  }}
                  onClick={() => setAssignDialog(msg.id)}
                >
                  <IconUserPlus size={14} style={{ marginRight: 6 }} /> Assign Task
                </Button>
              </Box>
            </Paper>
          );
        })}
      </Stack>

      {/* ── Incoming Message Rate Card ── */}
      <Paper
        elevation={0}
        sx={{
          mt: 4,
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid rgba(0,0,0,0.06)",
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        }}
      >
        <Box
          sx={{
            height: 6,
            background: "linear-gradient(90deg, #7C3AED, #A855F7, #C084FC)",
          }}
        />
        <Box sx={{ p: 3.5, textAlign: "center" }}>
          <Typography variant="body2" sx={{ color: "text.secondary", fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>
            Incoming Message Rate (last 30 days)
          </Typography>
          <Typography variant="h3" sx={{ color: "#7C3AED", mt: 1, fontWeight: 800 }}>
            {incomingRate}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", fontSize: 13 }}>
            messages / day
          </Typography>
        </Box>
      </Paper>

      {/* ── Assign Task Dialog ── */}
      <Dialog
        open={!!assignDialog}
        onClose={() => setAssignDialog(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            background: "linear-gradient(135deg, #7C3AED 0%, #A855F7 100%)",
            px: 3,
            py: 2.5,
          }}
        >
          <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700 }}>Assign Task</Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)", fontSize: 13, mt: 0.25 }}>
            Select a team member to assign this task to
          </Typography>
        </Box>
        <DialogContent sx={{ p: 0 }}>
          <List sx={{ py: 1 }}>
            {teamMembers.map((member) => (
              <ListItem
                key={member.email}
                sx={{
                  cursor: "pointer",
                  borderRadius: 2,
                  mx: 1,
                  mb: 0.5,
                  transition: "background 0.15s",
                  "&:hover": { bgcolor: "#F3F0FF" },
                }}
                onClick={() => handleAssign(member.email)}
              >
                <Avatar
                  sx={{
                    mr: 2,
                    width: 36,
                    height: 36,
                    background: "linear-gradient(135deg, #7C3AED, #A855F7)",
                    fontSize: 14,
                    fontWeight: 700,
                  }}
                >
                  {member.name?.[0]}
                </Avatar>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{member.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{member.email}</Typography>
                </Box>
              </ListItem>
            ))}
            {teamMembers.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: "center" }}>
                No team members found. Add members in Users & Groups.
              </Typography>
            )}
          </List>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid rgba(0,0,0,0.06)" }}>
          <Button style={{ borderRadius: 10, padding: "8px 20px", fontSize: 13 }} onClick={() => setAssignDialog(null)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
